import React from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client';
import { getStringTypeProps } from '@blaze-cms/utils/src/helpers';
import ListRender from './ListRender';
import { getSearchPublishedContent } from '../../application/query';
import {
  buildRawQuery,
  buildQueryFields,
  buildSetFilters,
  getGenericRenderVariables,
  getEntityData,
  getCurrentOffset,
  getUpdatedFilterBy,
  checkPropsToUse,
  buildPropsQuery
} from '../../helpers';
import { sortAggs, getItemsPerPageToUse, buildAzAggregations, getListComponent } from './helpers';
import { SEARCH_PUBLISHED_CONTENT, INFINITE } from '../../constants';
import { RAW_RESULTS } from './constants';

const ListBuilder = props => {
  const {
    mainSchemas,
    isCard,
    isAZ,
    inheritedFilters,
    paginationType,
    entities,
    parent: { itemId },
    itemsPerPage,
    itemsToDisplay,
    limit,
    propsToDisplay,
    offset,
    entityData,
    sortby,
    sort,
    paginationIndex,
    omitWrappers,
    operator,
    filterOperator,
    currentSchema,
    filterEntitySchema,
    requiredSchema,
    searchValues,
    azFilter,
    sortProperties,
    itemListIds
  } = props;

  const [entity] = entities;
  const isInfinite = paginationType === INFINITE;
  const listComponent = getListComponent(isCard, paginationType);
  const propsToDisplayValues = checkPropsToUse(propsToDisplay);
  const entitiesTypes = entities.map(ent => getEntityData(ent, mainSchemas[ent]));
  const graphqlEntityMap = entitiesTypes.reduce(
    (acc, entityType) => ({ ...acc, [entityType.entityType]: entityType.entity }),
    {}
  );
  const docTypes = entitiesTypes.map(({ docType: type }) => type);
  const entityTypes = entitiesTypes.map(({ entityType: eType }) => eType);
  const entityFields = buildPropsQuery(filterEntitySchema, propsToDisplayValues, props) || '';
  const itemsToDisplayToUse = itemListIds || itemsToDisplay;
  const limitToUse = limit || itemsToDisplayToUse.length;
  const shouldApplyDefaultSort = !sortProperties || !sortProperties.length;
  const itemsPerPageToUse = getItemsPerPageToUse(itemsPerPage, limitToUse);
  const initialOffset = getCurrentOffset(offset, itemsPerPageToUse, paginationIndex, limitToUse);
  const fields = buildQueryFields(entityFields, entityTypes, !isCard);
  const action = getSearchPublishedContent(fields);
  const actionKey = SEARCH_PUBLISHED_CONTENT;
  const updatedFilterBy = getUpdatedFilterBy(
    inheritedFilters,
    entityData,
    currentSchema,
    filterEntitySchema,
    itemId,
    props
  );

  const filterValues =
    updatedFilterBy && updatedFilterBy.length
      ? buildSetFilters({ ...props, filterBy: updatedFilterBy })
      : buildSetFilters({ ...props });

  const { relations = [] } = filterEntitySchema;
  const stringProps = getStringTypeProps(entity, relations, Object.values(requiredSchema));
  const filterData = {
    filterValues,
    filterOperator,
    relations,
    stringProps
  };
  const azRawQuery = buildRawQuery({
    docType: docTypes,
    operator,
    searchValues,
    filterData,
    id: itemId,
    itemsToDisplay: itemsToDisplayToUse,
    shouldApplySort: shouldApplyDefaultSort,
    azOptions: { shouldReturnAggs: true, isAZ, sortProperties }
  });

  const filterProps = {
    ...props,
    limit: itemsPerPageToUse,
    offset: initialOffset,
    sort,
    sortby,
    sortProperties
  };

  const propsForVariables = {
    relations,
    filterProps,
    stringProps
  };

  const azVariables = getGenericRenderVariables({
    ...propsForVariables,
    rawQuery: azRawQuery
  });

  const azAction = getSearchPublishedContent(RAW_RESULTS);
  const { data, error, loading } = useQuery(azAction, {
    variables: azVariables,
    skip: !isAZ
  });
  if (loading) return '';
  if (error) return error.message;

  const listAggregations = isAZ ? sortAggs(data) : [];
  const updatedAggregations = buildAzAggregations(listAggregations);

  const rawQuery = buildRawQuery({
    docType: docTypes,
    operator,
    searchValues,
    filterData: {
      ...filterData,
      azFilter
    },
    id: itemId,
    itemsToDisplay: itemsToDisplayToUse,
    shouldApplySort: shouldApplyDefaultSort,
    azOptions: { shouldReturnAggs: false, isAZ, sortProperties },
    sortProperties
  });

  const variables = getGenericRenderVariables({
    ...propsForVariables,
    rawQuery
  });

  return (
    <ListRender
      variables={variables}
      isInfinite={isInfinite}
      initialOffset={initialOffset}
      actionKey={actionKey}
      limitToUse={limitToUse}
      itemsPerPageToUse={itemsPerPageToUse}
      action={action}
      omitWrappers={omitWrappers}
      listComponent={listComponent}
      listAggregations={updatedAggregations}
      isAZ={isAZ}
      azFilter={azFilter}
      graphqlEntityMap={graphqlEntityMap}
      {...props}
      itemsToDisplay={itemsToDisplayToUse}
    />
  );
};

ListBuilder.propTypes = {
  entities: PropTypes.array.isRequired,
  mainSchemas: PropTypes.object.isRequired,
  isCard: PropTypes.bool.isRequired,
  isAZ: PropTypes.bool.isRequired,
  parent: PropTypes.object.isRequired,
  itemsPerPage: PropTypes.number.isRequired,
  paginationIndex: PropTypes.number.isRequired,
  propsToDisplay: PropTypes.array.isRequired,
  currentSchema: PropTypes.object.isRequired,
  filterEntitySchema: PropTypes.object.isRequired,
  requiredSchema: PropTypes.object.isRequired,
  searchValues: PropTypes.object.isRequired,
  inheritedFilters: PropTypes.array,
  entityData: PropTypes.object,
  itemsToDisplay: PropTypes.array,
  limit: PropTypes.number,
  omitWrappers: PropTypes.bool,
  offset: PropTypes.number,
  sortby: PropTypes.array,
  sort: PropTypes.string,
  operator: PropTypes.string.isRequired,
  filterOperator: PropTypes.string.isRequired,
  paginationType: PropTypes.string,
  azFilter: PropTypes.string,
  sortProperties: PropTypes.array,
  itemListIds: PropTypes.array
};

ListBuilder.defaultProps = {
  sort: '',
  sortby: [],
  entityData: {},
  itemsToDisplay: [],
  itemListIds: null,
  limit: 0,
  omitWrappers: false,
  offset: 0,
  paginationType: 'infinite',
  inheritedFilters: [],
  azFilter: '',
  sortProperties: []
};

export default ListBuilder;
